import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';

/**
 * Automated review requests: one per fully paid invoice, sent after a
 * per-company delay by the review-request-send edge function.
 *
 *   review_request_settings  one row per company; absent = off
 *   review_requests          the queue and log the office sees
 *   brands.review_url        optional per-trading-name link
 */

export type ReviewChannel = 'sms' | 'email' | 'both';
/** direct = {link} is the review site; stars = {link} is our hosted 5-star page with a link per star. */
export type ReviewLinkMode = 'direct' | 'stars';
/** Keys '1'..'5'; a missing star shows the thank-you screen with a comment box. */
export type StarLinks = Partial<Record<'1' | '2' | '3' | '4' | '5', string>>;

export interface ReviewRequestSettings {
  company_id: string;
  is_enabled: boolean;
  delay_minutes: number;
  channel: ReviewChannel;
  review_url: string | null;
  sms_template: string;
  email_subject: string;
  email_template: string;
  quiet_hours_start: number;
  quiet_hours_end: number;
  cooldown_days: number;
  link_mode: ReviewLinkMode;
  star_links: StarLinks;
  star_prompt: string;
}

export type ReviewRequestSettingsInput = Omit<ReviewRequestSettings, 'company_id'>;

/** Mirrors the column defaults in 20260928120000_review_requests.sql so the form is filled before the first save. */
export const DEFAULT_REVIEW_SETTINGS: ReviewRequestSettingsInput = {
  is_enabled: false,
  delay_minutes: 120,
  channel: 'sms',
  review_url: null,
  sms_template:
    'Hi {first_name}, thanks for choosing {company}. Could you spare 30 seconds to tell us how we did? {link}',
  email_subject: 'How did we do, {first_name}?',
  email_template:
    'Hi {first_name},\n\nThanks for choosing {company}. Could you spare 30 seconds to tell us how we did?\n\n{link}\n\nThanks,\n{company}',
  quiet_hours_start: 8,
  quiet_hours_end: 20,
  cooldown_days: 90,
  link_mode: 'direct',
  star_links: {},
  star_prompt: 'How did we do?',
};

const SETTINGS_COLUMNS =
  'company_id, is_enabled, delay_minutes, channel, review_url, sms_template, email_subject, email_template, quiet_hours_start, quiet_hours_end, cooldown_days, link_mode, star_links, star_prompt';

export function useReviewRequestSettings() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  const query = useQuery({
    queryKey: ['review-request-settings', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<ReviewRequestSettings | null> => {
      const { data, error } = await supabase
        .from('review_request_settings')
        .select(SETTINGS_COLUMNS)
        .eq('company_id', tenantId!)
        .maybeSingle();
      if (error) throw error;
      return (data as ReviewRequestSettings | null) ?? null;
    },
  });

  const save = useMutation({
    mutationFn: async (input: ReviewRequestSettingsInput) => {
      if (!tenantId) throw new Error('No company selected');
      const { error } = await supabase
        .from('review_request_settings')
        .upsert({ company_id: tenantId, ...input }, { onConflict: 'company_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-request-settings', tenantId] });
    },
  });

  return { settings: query.data ?? null, isLoading: query.isLoading, save: save.mutateAsync, isSaving: save.isPending };
}

export interface BrandReviewLink {
  id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  review_url: string | null;
}

/** Trading names with their review link, kept apart from useBrands so its cached shape is untouched. */
export function useBrandReviewLinks() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['brand-review-links'],
    queryFn: async (): Promise<BrandReviewLink[]> => {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name, is_default, is_active, review_url')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');
      if (error) throw error;
      return (data ?? []) as BrandReviewLink[];
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, review_url }: { id: string; review_url: string | null }) => {
      const { error } = await supabase.from('brands').update({ review_url }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-review-links'] });
    },
  });

  return { brands: query.data ?? [], isLoading: query.isLoading, update: update.mutateAsync, isUpdating: update.isPending };
}

export type ReviewRequestStatus = 'queued' | 'sending' | 'sent' | 'skipped' | 'cancelled' | 'failed';

export interface ReviewRequestRow {
  id: string;
  status: ReviewRequestStatus;
  channel: ReviewChannel;
  send_after: string;
  sent_at: string | null;
  to_phone: string | null;
  to_email: string | null;
  skip_reason: string | null;
  error: string | null;
  attempts: number;
  rating: number | null;
  rating_comment: string | null;
  created_at: string;
  job_id: string | null;
  customer_id: string | null;
  email_message_id: string | null;
  invoices: { invoice_number: string } | null;
  customers: { customer_name: string } | null;
  /** Postmark open / click events for the email, merged in by the hook. */
  email_opened_at?: string | null;
  email_clicked_at?: string | null;
}

export function useReviewRequestLog(limit = 50) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  const query = useQuery({
    queryKey: ['review-requests', tenantId, limit],
    enabled: !!tenantId,
    refetchInterval: 60_000,
    queryFn: async (): Promise<ReviewRequestRow[]> => {
      const { data, error } = await supabase
        .from('review_requests')
        .select(
          'id, status, channel, send_after, sent_at, to_phone, to_email, skip_reason, error, attempts, rating, rating_comment, created_at, job_id, customer_id, email_message_id, invoices (invoice_number), customers (customer_name)',
        )
        .eq('company_id', tenantId!)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      const rows = (data ?? []) as unknown as ReviewRequestRow[];

      // Opens and clicks live on the email record (filled by postmark-events).
      // No FK to embed through, so one extra read keyed by id.
      const emailIds = rows.map(r => r.email_message_id).filter((id): id is string => !!id);
      if (emailIds.length > 0) {
        const { data: emails } = await supabase
          .from('email_messages')
          .select('id, opened_at, clicked_at')
          .in('id', emailIds);
        const byId = new Map((emails ?? []).map(e => [e.id, e]));
        for (const r of rows) {
          const e = r.email_message_id ? byId.get(r.email_message_id) : undefined;
          r.email_opened_at = e?.opened_at ?? null;
          r.email_clicked_at = e?.clicked_at ?? null;
        }
      }
      return rows;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['review-requests', tenantId] });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const { data: auth } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('review_requests')
        .update({ status: 'cancelled', skip_reason: 'cancelled_by_user', cancelled_by: auth.user?.id ?? null })
        .eq('id', id)
        .eq('status', 'queued')
        .select('id')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('This request has already been sent or cancelled');
    },
    onSuccess: invalidate,
  });

  const sendNow = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke('review-request-send', { body: { review_request_id: id } });
      if (error) throw new Error(error.message || 'Could not send');
      if (data?.error) throw new Error(String(data.error));
      return data as { ok: boolean; outcome: { status: string; reason?: string; error?: string } };
    },
    onSuccess: invalidate,
  });

  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    cancel: cancel.mutateAsync,
    isCancelling: cancel.isPending,
    sendNow: sendNow.mutateAsync,
    isSending: sendNow.isPending,
    refetch: query.refetch,
  };
}

export interface TestSendResult {
  ok: boolean;
  results: { sms?: string; email?: string };
  preview: { sms: string; email: string };
}

/** Sends the current templates to the signed-in admin so the wording can be checked. */
export function useSendTestReviewRequest() {
  return useMutation({
    mutationFn: async (input: { channel?: ReviewChannel; to_phone?: string; to_email?: string }) => {
      const { data, error } = await supabase.functions.invoke('review-request-send', { body: { test: true, ...input } });
      if (error) throw new Error(error.message || 'Could not send the test');
      if (data?.error) throw new Error(String(data.error));
      return data as TestSendResult;
    },
  });
}

/** Plain-English reason for a skipped / cancelled / failed row. */
export function describeSkipReason(reason: string | null | undefined): string {
  switch (reason) {
    case 'opted_out': return 'Customer opted out of review requests';
    case 'cooldown': return 'Customer was asked recently';
    case 'no_customer': return 'Invoice has no customer record';
    case 'no_job': return 'Invoice has no job';
    case 'no_contact': return 'No mobile or email on the customer';
    case 'no_review_url': return 'No review link set';
    case 'disabled': return 'Automation was off when it came due';
    case 'not_paid': return 'Invoice was no longer paid';
    case 'refunded': return 'Invoice was refunded';
    case 'disputed': return 'Payment was disputed';
    case 'cancelled_by_user': return 'Cancelled by the office';
    case 'no_company': return 'Company not found';
    default: return reason ? reason.replace(/_/g, ' ') : '';
  }
}

/** Splits minutes into the largest whole unit for the delay picker. */
export function minutesToUnit(minutes: number): { value: number; unit: 'minutes' | 'hours' | 'days' } {
  if (minutes > 0 && minutes % 1440 === 0) return { value: minutes / 1440, unit: 'days' };
  if (minutes > 0 && minutes % 60 === 0) return { value: minutes / 60, unit: 'hours' };
  return { value: minutes, unit: 'minutes' };
}

export function unitToMinutes(value: number, unit: 'minutes' | 'hours' | 'days'): number {
  const n = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
  return unit === 'days' ? n * 1440 : unit === 'hours' ? n * 60 : n;
}
